import React from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import '../styles/lookup.scss';

import CloseIcon from '@mui/icons-material/Close';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const DEFAULT_ZOOM = 15;

function locateCentroid(locations) {
  if (locations.length === 0) {
    return {
      lat: 40.300929,
      lng: -75.9799449,
    };
  }

  const latAvg = locations.reduce((total, loc) => total + loc.latitude, 0) / locations.length;
  const lngAvg = locations.reduce((total, loc) => total + loc.longitude, 0) / locations.length;

  return { lat: latAvg, lng: lngAvg };
}

function lngOffsetForPixelOffset(lat, zoom, pixelOffset) {
  const lngPerPixel = 360 / (256 * (2 ** zoom) * Math.cos((lat * Math.PI) / 180));
  return pixelOffset * lngPerPixel;
}

function mapCenter(addresses, centerOffsetX = 0, zoom = DEFAULT_ZOOM) {
  const centroid = locateCentroid(addresses);
  if (!centerOffsetX) return centroid;

  return {
    lat: centroid.lat,
    lng: centroid.lng - lngOffsetForPixelOffset(centroid.lat, zoom, centerOffsetX),
  };
}

function MapsComponent(props) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAycfeEVduEWMYSL2u_ujkAbUywvy3qcSY',
  });

  const [map, setMap] = React.useState(null);
  const [selectedAddress, setSelectedAddress] = React.useState(null);
  const centerOffsetX = props.centerOffsetX || 0;

  const center = React.useMemo(
    () => mapCenter(props.addresses, centerOffsetX, DEFAULT_ZOOM),
    [props.addresses, centerOffsetX]
  );

  const applyCenter = React.useCallback((mapInstance) => {
    window.google.maps.event.trigger(mapInstance, 'resize');
    mapInstance.setCenter(center);
    mapInstance.setZoom(DEFAULT_ZOOM);
  }, [center]);

  const onLoad = React.useCallback((mapInstance) => {
    setMap(mapInstance);
    applyCenter(mapInstance);
    window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
      applyCenter(mapInstance);
    });
  }, [applyCenter]);

  const onUnmount = React.useCallback(() => {
    setMap(null);
  }, []);

  React.useEffect(() => {
    if (!map || !centerOffsetX) return undefined;

    applyCenter(map);
    const timeoutId = window.setTimeout(() => applyCenter(map), 150);

    return () => window.clearTimeout(timeoutId);
  }, [map, centerOffsetX, applyCenter]);

  function formatAddressString(addr) {
    return `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`;
  }

  function equipmentAtLocation(address) {
    return (props.equipment || []).filter((e) => e.address.id === address.id);
  }

  return isLoaded ? (
    <GoogleMap
      mapContainerClassName="lookup-map-view"
      mapContainerStyle={containerStyle}
      center={center}
      zoom={DEFAULT_ZOOM}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{ scrollwheel: false }}
    >
      {props.addresses.map((a) => (
        <Marker
          key={a.id}
          position={{ lat: a.latitude, lng: a.longitude }}
          onClick={() => setSelectedAddress(a)}
        />
      ))}
      {selectedAddress ? (
        <div className="lookup-map-popup-backdrop" onClick={() => setSelectedAddress(null)}>
          <div
            className="lookup-map-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Location details"
          >
            <button
              type="button"
              className="lookup-map-popup-close"
              onClick={() => setSelectedAddress(null)}
              aria-label="Close"
            >
              <CloseIcon />
            </button>

            <div className="lookup-map-popup-body">
              <span className="lookup-map-popup-address">{formatAddressString(selectedAddress)}</span>
              {selectedAddress.description?.length > 0 ? (
                <span className="lookup-map-popup-desc">{selectedAddress.description}</span>
              ) : null}
            </div>

            <div className="lookup-map-popup-footer">
              <a
                target="_blank"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatAddressString(selectedAddress))}`}
                className="lookup-map-popup-link"
                rel="noopener noreferrer"
              >
                Open in Maps
              </a>
              <span className="lookup-map-popup-systems">
                {equipmentAtLocation(selectedAddress).length} system
                {equipmentAtLocation(selectedAddress).length === 1 ? '' : 's'} on site
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </GoogleMap>
  ) : null;
}

export default React.memo(MapsComponent);
